import React, { useState } from 'react';
import { config } from '../config';
import './EmailForm.css';

const DESIGN_SYSTEM_PRIMARY = '#4e7c7f';
const LEGACY_BLUE_BUTTON_COLORS = new Set(['4a90e2', '3b82f6', '4285f4', '2563eb', '3367d6']);

const normalizeActionColor = (value) => {
  const normalizedValue = typeof value === 'string' ? value.trim().toLowerCase().replace(/^#/, '') : '';
  if (!normalizedValue || LEGACY_BLUE_BUTTON_COLORS.has(normalizedValue)) {
    return DESIGN_SYSTEM_PRIMARY;
  }

  return value;
};

const getFormMaxWidth = (width) => {
  if (width === 'narrow') return '500px';
  if (width === 'wide') return '800px';
  return '600px';
};

const buildFormStyle = (props) => ({
  '--email-form-primary': normalizeActionColor(props.primaryColor || props.buttonColor),
  '--email-form-button-text': props.buttonTextColor || '#ffffff',
  '--email-form-text': props.textColor || 'var(--color-text-primary, #1c1c1a)',
  '--email-form-background': props.backgroundColor || 'var(--color-bg-surface, #ffffff)',
  '--email-form-max-width': getFormMaxWidth(props.width),
});

const parseJsonSafely = (value) => {
  if (!value) {
    return null;
  }

  try {
    return JSON.parse(value);
  } catch (error) {
    return null;
  }
};

const getEmailErrorMessage = ({ code, status, message }) => {
  if (code === 'EMAIL_PROVIDER_BLOCKED') {
    return 'Имейл услугата е конфигурирана със SMTP, което не се поддържа на Render Free. Администраторът трябва да премине към Resend API или да обнови плана.';
  }

  if (code === 'EMAIL_PROVIDER_CONFIG_ERROR') {
    return 'Имейл услугата не е конфигурирана правилно. Моля, свържете се с администратора на уебсайта.';
  }

  if (code === 'EMAIL_PROVIDER_TIMEOUT') {
    return 'Връзката с имейл услугата изтече. Моля, опитайте отново след малко.';
  }

  if (status === 504) {
    return 'Сървърът не отговори навреме. Моля, опитайте отново след малко.';
  }

  if (status === 503) {
    return 'Имейл услугата е временно недостъпна. Моля, опитайте отново по-късно.';
  }

  if (typeof message === 'string' && message.trim()) {
    return message;
  }

  return 'Неуспешно изпращане. Моля, опитайте отново.';
};

const EmailForm = ({ section }) => {
  const props = section?.props || {};
  const sectionId = section?.id || 'email-form';
  const [formData, setFormData] = useState({
    name: '',
    message: '',
    confirmAttendance: false
  });
  const [status, setStatus] = useState({ type: '', message: '' });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ type: 'loading', message: 'Изпращане...' });

    if (!props.hostEmail) {
      setStatus({ type: 'error', message: 'Имейлът на хоста не е конфигуриран. Моля, свържете се с администратора на уебсайта.' });
      return;
    }

    const htmlContent = `
      <div style="font-family: 'Open Sans', Arial, sans-serif; max-width: 600px; margin: 0 auto; background-color: #f9f9f9; border: 1px solid #d3d3d3; border-radius: 10px; overflow: hidden;">
        <!-- Header Banner -->
        <div style="background-color: #e8ecef; text-align: center; padding: 20px;">
        </div>
        <!-- Content -->
        <div style="padding: 20px; background-color: #f9f9f9;">
          <h2 style="font-family: 'Lora', serif; color: #333333; text-align: center; margin-bottom: 20px;">Нов RSVP от Нашата Сватба</h2>
          <p style="color: #666666; font-size: 16px; line-height: 1.5;">Получихме нов отговор за вашата сватба:</p>
          <table style="width: 100%; border-collapse: collapse; margin: 20px 0;">
            <tr>
              <td style="padding: 10px; font-weight: bold; color: #333333; font-size: 16px; border-bottom: 1px solid #d3d3d3;">Име:</td>
              <td style="padding: 10px; color: #666666; font-size: 16px; border-bottom: 1px solid #d3d3d3;">${formData.name}</td>
            </tr>
            <tr>
              <td style="padding: 10px; font-weight: bold; color: #333333; font-size: 16px; border-bottom: 1px solid #d3d3d3;">Съобщение:</td>
              <td style="padding: 10px; color: #666666; font-size: 16px; border-bottom: 1px solid #d3d3d3;">${formData.message}</td>
            </tr>
            <tr>
              <td style="padding: 10px; font-weight: bold; color: #333333; font-size: 16px; border-bottom: 1px solid #d3d3d3;">Потвърждение за присъствие:</td>
              <td style="padding: 10px; color: #666666; font-size: 16px; border-bottom: 1px solid #d3d3d3;">${formData.confirmAttendance ? 'Да' : 'Не'}</td>
            </tr>
          </table>
          <div style="text-align: center; margin-top: 20px;">
            <a href="https://nashatasvatba.in" style="display: inline-block; padding: 10px 20px; background-color: #8b8b7a; color: #ffffff; text-decoration: none; border-radius: 5px; font-size: 16px;">Вижте повече за сватбата</a>
          </div>
        </div>
        <!-- Footer -->
        <div style="background-color: #e8ecef; text-align: center; padding: 10px; font-size: 14px; color: #333333;">
          <p>Благодарим ви, че използвате Нашата Сватба!</p>
          <p><a href="https://nashatasvatba.in" style="color: #333333; text-decoration: underline;">Нашата Сватба</a></p>
        </div>
        <!-- Responsive Design -->
        <style>
          @media only screen and (max-width: 600px) {
            div { padding: 10px !important; }
            h2 { font-size: 20px !important; }
            table td { font-size: 14px !important; }
            a { font-size: 14px !important; padding: 8px 16px !important; }
          }
        </style>
      </div>
    `;

    try {
      const response = await fetch(config.endpoints.sendEmail, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          to: props.hostEmail,
          subject: `Нов RSVP от ${formData.name} - Нашата Сватба`,
          text: `
Име: ${formData.name}
Съобщение: ${formData.message}
Потвърждавам присъствието си: ${formData.confirmAttendance ? 'Да' : 'Не'}
          `,
          html: htmlContent
        }),
      });

      if (!response.ok) {
        const rawErrorBody = await response.text();
        const errorData = parseJsonSafely(rawErrorBody);
        const errorMessage = getEmailErrorMessage({
          code: errorData?.code,
          status: response.status,
          message: errorData?.error,
        });
        const requestError = new Error(errorMessage);
        requestError.code = errorData?.code;
        requestError.httpStatus = response.status;
        requestError.provider = errorData?.provider;
        throw requestError;
      }

      setStatus({ type: 'success', message: 'Благодарим за отговора!' });
      setFormData({ name: '', message: '', confirmAttendance: false });
    } catch (error) {
      console.error('Error sending email:', error);
      setStatus({ type: 'error', message: error.message || 'Неуспешно изпращане. Моля, опитайте отново.' });
    }
  };

  const getFormClasses = () => {
    return [
      'email-form-card',
      props.style ? `email-form-card--${props.style}` : '',
      props.width ? `email-form-card--${props.width}` : '',
    ].filter(Boolean).join(' ');
  };

  const getStatusMessage = (message) => {
    const translations = {
      'Sending...': 'Изпращане...',
      'Host email is not configured. Please contact the website administrator.': 'Имейлът на хоста не е конфигуриран. Моля, свържете се с администратора на уебсайта.',
      'Thank you for your response!': 'Благодарим за отговора!',
      'Failed to send. Please try again.': 'Неуспешно изпращане. Моля, опитайте отново.'
    };
    return translations[message] || message;
  };

  return (
    <div className={getFormClasses()} style={buildFormStyle(props)}>
      <h2 className="email-form-title">
        {props.title || 'RSVP Форма'}
      </h2>
      <p className="email-form-description">
        {props.description || 'Моля, попълнете тази форма, за да потвърдите присъствието си.'}
      </p>

      <form onSubmit={handleSubmit} className="email-form">
        <div className="email-form-field">
          <label className="email-form-label" htmlFor={`${sectionId}-name`}>Вашето име</label>
          <input
            id={`${sectionId}-name`}
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            autoComplete="name"
            className="email-form-input"
            placeholder="Въведете вашето име"
          />
        </div>

        <div className="email-form-field">
          <label className="email-form-label" htmlFor={`${sectionId}-message`}>Вашето съобщение</label>
          <textarea
            id={`${sectionId}-message`}
            name="message"
            value={formData.message}
            onChange={handleChange}
            required
            rows={4}
            className="email-form-textarea"
            placeholder="Въведете вашето съобщение"
          />
        </div>

        <div className="email-form-check-row">
          <label className="email-form-checkbox">
            <input
              type="checkbox"
              name="confirmAttendance"
              checked={formData.confirmAttendance}
              onChange={handleChange}
              className="email-form-checkbox-input"
            />
            <span>Потвърждавам присъствието си</span>
          </label>
        </div>

        <button type="submit" className="email-form-submit" disabled={status.type === 'loading'}>
          Изпрати отговор
        </button>

        {status.type && (
          <div className={`email-form-status email-form-status--${status.type}`}>
            {getStatusMessage(status.message)}
          </div>
        )}
      </form>
    </div>
  );
};

export default EmailForm;
